import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import shortid from 'shortid';
import favicon16 from '../../static/icons/favicon-16x16.png';
import favicon32 from '../../static/icons/favicon-32x32.png';
import favicon96 from '../../static/icons/favicon-96x96.png';
import useSiteMetadata from '../graphql/staticQueries/useSiteMetadata';
import defaultImage from '../../assets/resources/optimized-images/global-seo-img.png';

function SEO({
  description: specDescription,
  title: specTitle,
  image: specImage,
  specificUrl
}) {
  const {
    siteUrl,
    shortName,
    twitterUrl,
    themeColor,
    title: siteTitle,
    description: siteDescription
  } = useSiteMetadata();

  // if title and description is not provided, use those from siteMetadata
  const title = specTitle || siteTitle;
  const description = specDescription || siteDescription;
  const image = specImage;
  let twitterImage = image;
  if (!image.startsWith(siteUrl)) {
    twitterImage = `${siteUrl}${image}`;
  }
  return (
    <Helmet>
      {/* General tags */}
      <meta charset="utf-8" />
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="HandheldFriendly" content="True" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      {image && <meta name="image" content={image} />}
      {/* Favicons */}
      <link rel="icon" type="image/png" sizes="16x16" href={`${favicon16}`} />
      <link rel="icon" type="image/png" sizes="32x32" href={`${favicon32}`} />
      <link rel="icon" type="image/png" sizes="96x96" href={`${favicon96}`} />
      <link rel="shortcut icon" type="image/png" href={`${favicon96}`} />
      {/* OpenGraph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={shortName} />
      <meta property="og:url" content={specificUrl || siteUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pl_PL" />
      {image && <meta property="og:image" content={image} />}
      {!image && <meta property="og:image" content={defaultImage} />}
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:site" content={twitterUrl} />
      {image && (
        <meta
          name="twitter:image"
          content={`${twitterImage}?uid=${shortid.generate()}`}
        />
      )}
      <meta name="twitter:image:width" content="1200" />
      <meta name="twitter:image:height" content="630" />
      <meta name="twitter:image:alt" content={title} />
      {/* Other tags */}
      <meta name="theme-color" content={themeColor} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={shortName} />
      <meta
        data-hid="author"
        name="author"
        content="Michal <michal.popiel@ulam.io> Adrian <adrian@ulam.io>"
      />
      <meta name="msapplication-config" content="browserconfig.xml" />
      {/* Preconnects */}
      <link href="https://www.googleadservices.com" rel="preconnect" />
      <link href="https://www.googletagmanager.com" rel="preconnect" />
    </Helmet>
  );
}

SEO.defaultProps = {
  description: '',
  title: '',
  image: ''
};

SEO.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string,
  image: PropTypes.string
};

export default SEO;
